import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, CheckCircle2, XCircle, BookCheck, ChevronDown } from "lucide-react";
import { api, download, errMsg, eur, fdate, fdatetime, fileUrl, can } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable, Field, PageHeader } from "@/components/common";

const y = new Date().getFullYear();
const TABS = [["pending", "À comptabiliser"], ["history", "Historique des exports"], ["schedule", "Échéancier"], ["immos", "Immobilisations"]];

function Period({ p, setP }) {
  return <div className="flex flex-wrap gap-3">
    <Field label="Du"><input type="date" className="inp" value={p.start} onChange={(e) => setP({ ...p, start: e.target.value })} data-testid="period-start-input" /></Field>
    <Field label="Au"><input type="date" className="inp" value={p.end} onChange={(e) => setP({ ...p, end: e.target.value })} data-testid="period-end-input" /></Field>
  </div>;
}

function Pending({ p, setP, canExport }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const load = () => api.get("/accounting/pending", { params: p }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [p.start, p.end]); // eslint-disable-line
  const run = async () => {
    if (!window.confirm(`Comptabiliser ${rows.length} facture(s) ? Elles seront verrouillées.`)) return;
    setBusy(true);
    try { const r = await api.post("/accounting/export", p); setRes(r.data); toast.success(`${r.data.count} facture(s) comptabilisée(s)`); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const unbalanced = rows.some((r) => !r.balanced);
  return (
    <div className="space-y-5">
      <div className="card flex flex-wrap items-end gap-4 p-5">
        <Period p={p} setP={setP} />
        <div className="ml-auto text-right text-sm"><p className="text-[#6B6B80]">{rows.length} facture(s) « Bon à payer » ou « Payée »</p><p className="num text-xl font-extrabold">{eur(rows.reduce((a, r) => a + r.invoice.total_ttc, 0))} TTC</p></div>
        {canExport && <button className="btn-primary" disabled={!rows.length || unbalanced || busy} onClick={run} data-testid="accounting-export-btn"><BookCheck size={16} /> Générer l'export comptable</button>}
      </div>
      {unbalanced && <p className="text-sm font-semibold text-[#B03A3A]" data-testid="unbalanced-warning">Export bloqué : au moins une écriture n'est pas équilibrée.</p>}
      {res && <div className="card flex flex-wrap items-center gap-3 border-l-4 border-l-[#2E7D5B] p-4 text-sm" data-testid="export-result">
        <CheckCircle2 className="text-[#2E7D5B]" /> Export généré : {res.count} factures, {res.lines} écritures, pièces {res.pieces}.
        <a className="btn-secondary ml-auto" href={fileUrl(res.xlsx_file_id)} data-testid="export-download-xlsx"><FileSpreadsheet size={15} /> Excel</a>
        <a className="btn-secondary" href={fileUrl(res.csv_file_id)} data-testid="export-download-csv"><FileText size={15} /> CSV</a>
      </div>}
      <div className="card divide-y divide-[#E4E4EC]" data-testid="pending-list">
        {rows.map((r, i) => (
          <div key={r.invoice.id}>
            <button className="flex w-full items-center gap-4 px-4 py-3 text-left text-sm hover:bg-[#A5894E]/10" onClick={() => setOpen(open === i ? null : i)} data-testid={`pending-row-${i}`}>
              {r.balanced ? <CheckCircle2 size={16} className="text-[#2E7D5B]" /> : <XCircle size={16} className="text-[#B03A3A]" />}
              <span className="w-28 num">{fdate(r.invoice.date)}</span><span className="flex-1 font-semibold">{r.invoice.number} – {r.invoice.supplier_name}</span>
              <span className="num font-bold">{eur(r.invoice.total_ttc)}</span><ChevronDown size={16} className={open === i ? "rotate-180" : ""} />
            </button>
            {open === i && <div className="overflow-x-auto bg-[#F5F5F8] p-3"><table className="dt"><thead><tr><th>Compte</th><th>Auxiliaire</th><th>Libellé</th><th className="text-right">Débit</th><th className="text-right">Crédit</th><th>Analytique</th></tr></thead>
              <tbody>{r.entries.map((e, j) => <tr key={j}><td className="mono">{e.account}</td><td className="mono">{e.aux}</td><td>{e.label}</td><td className="num text-right">{e.debit ? eur(e.debit) : ""}</td><td className="num text-right">{e.credit ? eur(e.credit) : ""}</td><td>{e.analytic}</td></tr>)}</tbody></table></div>}
          </div>
        ))}
        {!rows.length && <p className="p-8 text-center text-sm text-[#6B6B80]">Aucune facture à comptabiliser sur la période.</p>}
      </div>
    </div>
  );
}

function History() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/accounting/exports").then((r) => setRows(r.data)); }, []);
  return <DataTable testid="exports-table" rows={rows} columns={[
    { key: "at", label: "Date d'export", value: (r) => fdatetime(r.at) }, { key: "by", label: "Par" }, { key: "period", label: "Période", value: (r) => `${fdate(r.start)} → ${fdate(r.end)}` },
    { key: "count", label: "Factures" }, { key: "pieces", label: "Pièces" }, { key: "total", label: "Total TTC", type: "money" },
    { key: "dl", label: "Fichiers", sortable: false, export: false, render: (r) => <span className="flex gap-1"><a className="btn-ghost" href={fileUrl(r.xlsx_file_id)} data-testid={`redownload-xlsx-${r.id}`}><FileSpreadsheet size={15} /></a><a className="btn-ghost" href={fileUrl(r.csv_file_id)}><FileText size={15} /></a></span> }]} />;
}

function Schedule() {
  const [rows, setRows] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/invoices", { params: { status: "bon_a_payer" } }).then((r) => setRows(r.data.sort((a, b) => a.due_date.localeCompare(b.due_date)))); }, []);
  return <div className="space-y-4">
    <div className="flex justify-end"><button className="btn-secondary" onClick={() => download("/extractions/run", { type: "echeancier" })} data-testid="schedule-export-btn"><FileSpreadsheet size={15} /> Exporter l'échéancier</button></div>
    <DataTable testid="schedule-table" rows={rows} onRowClick={(r) => nav(`/factures/${r.id}`)} columns={[{ key: "due_date", label: "Échéance", type: "date", render: (r) => <span className={`num ${r.overdue ? "font-bold text-[#B03A3A]" : ""}`}>{fdate(r.due_date)}</span> }, { key: "supplier_name", label: "Fournisseur" }, { key: "number", label: "N° facture" }, { key: "total_ttc", label: "TTC", type: "money" }]} />
  </div>;
}

function Immos({ p, setP }) {
  return <div className="card flex flex-wrap items-end gap-4 p-5">
    <Period p={p} setP={setP} />
    <button className="btn-primary ml-auto" onClick={() => download("/extractions/run", { type: "immobilisations", ...p })} data-testid="immos-export-btn"><FileSpreadsheet size={15} /> Exporter les immobilisations</button>
  </div>;
}

export default function Accounting() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [p, setP] = useState({ start: `${y}-01-01`, end: `${y}-12-31` });
  return (
    <div>
      <PageHeader title="Comptabilité" subtitle="Écritures d'achat, numérotation des pièces, export CSV / Excel et verrouillage des factures comptabilisées." />
      <div className="mb-6 flex flex-wrap gap-1 border-b border-[#E4E4EC]">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`accounting-tab-${k}`} className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold ${tab === k ? "border-[#A5894E]" : "border-transparent text-[#6B6B80]"}`}>{l}</button>)}
      </div>
      {tab === "pending" && <Pending p={p} setP={setP} canExport={can(user, "comptable")} />}
      {tab === "history" && <History />}
      {tab === "schedule" && <Schedule />}
      {tab === "immos" && <Immos p={p} setP={setP} />}
    </div>
  );
}
